import React, { useState, useEffect, useCallback } from 'react';
import { FaDownload, FaTrash, FaSearch, FaInfoCircle, FaTimes, FaThLarge, FaTh, FaThList, FaImages, FaStar, FaRegStar, FaTag, FaFolder, FaFolderOpen, FaCheckSquare, FaCheck, FaSort, FaGripLines, FaUpload } from 'react-icons/fa';
import Modal from 'react-modal';
import { saveAs } from 'file-saver';
import { GeneratedImage, Tag, ImageFolder } from '../types';
import ImagePreview from './ImagePreview'; // 导入图片预览组件
import ImageDetails from './ImageDetails'; // 导入图像详情组件
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';

// 导入模型数据，用于显示用户友好的模型名称
import { fetchModels } from '../services/api';

// 添加自定义样式
const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: '0',
    border: 'none',
    background: 'transparent',
  },
};

// 设置Modal的根元素
Modal.setAppElement('#root');

// 不使用defaultStyles属性，改用overlayClassName和className属性来设置样式

interface GalleryProps {
  images: GeneratedImage[];
  clearGallery: () => void;
  // 添加删除单张图片的方法
  removeImage?: (imageId: string) => void;
  // 标签相关
  tags: Tag[];
  createTag: (name: string, color?: string) => string;
  deleteTag: (tagId: string) => void;
  addTagToImage: (imageId: string, tagId: string) => void;
  removeTagFromImage: (imageId: string, tagId: string) => void;
  selectedTags: string[];
  setSelectedTags: React.Dispatch<React.SetStateAction<string[]>>;
  // 收藏夹相关
  folders: ImageFolder[];
  createFolder: (name: string, description?: string) => string;
  deleteFolder: (folderId: string) => void;
  addImageToFolder: (imageId: string, folderId: string) => void;
  removeImageFromFolder: (imageId: string, folderId: string) => void;
  selectedFolder: string | null;
  setSelectedFolder: React.Dispatch<React.SetStateAction<string | null>>;
  // 收藏功能
  showFavorites: boolean;
  setShowFavorites: React.Dispatch<React.SetStateAction<boolean>>;
  toggleFavorite: (imageId: string) => void;
  // 数据备份和恢复功能
  exportData: () => void;
  importData: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const Gallery: React.FC<GalleryProps> = ({ 
  images, 
  clearGallery, 
  removeImage,
  tags,
  createTag,
  deleteTag,
  addTagToImage,
  removeTagFromImage,
  selectedTags,
  setSelectedTags,
  folders,
  createFolder,
  deleteFolder,
  addImageToFolder,
  removeImageFromFolder,
  selectedFolder,
  setSelectedFolder,
  showFavorites,
  setShowFavorites,
  toggleFavorite,
  exportData,
  importData
}) => {
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterModel, setFilterModel] = useState<string>('');
  const [filterAspectRatio, setFilterAspectRatio] = useState<string>('');
  const [filterDate, setFilterDate] = useState<string>('');
  const [modelNameMap, setModelNameMap] = useState<Record<string, string>>({});
  
  // 添加缩略图尺寸控制状态
  const [thumbnailSize, setThumbnailSize] = useState<string>('medium'); // 'small', 'medium', 'large', 'xlarge'

  // 图片预览状态
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageIndex, setPreviewImageIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  
  // 批量操作相关状态
  const [batchActionMenuOpen, setBatchActionMenuOpen] = useState<boolean>(false);
  const [batchTagMenuOpen, setBatchTagMenuOpen] = useState<boolean>(false);
  const [batchFolderMenuOpen, setBatchFolderMenuOpen] = useState<boolean>(false);
  
  // 图片比较相关状态
  const [compareMode, setCompareMode] = useState<boolean>(false);
  const [compareImages, setCompareImages] = useState<GeneratedImage[]>([]);
  
  // 高级导出相关状态
  const [exportMenuOpen, setExportMenuOpen] = useState<boolean>(false);
  const [exportFormat, setExportFormat] = useState<'png' | 'jpeg' | 'webp'>('png');
  const [exportQuality, setExportQuality] = useState<number>(90);
  const [exportNameTemplate, setExportNameTemplate] = useState<string>('ai-painting-{id}');
  
  // 拖拽排序相关状态
  const [sortMode, setSortMode] = useState<boolean>(false);
  const [customSortedImages, setCustomSortedImages] = useState<GeneratedImage[]>([]);
  
  // 高级搜索相关状态
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState<boolean>(false);
  const [advancedSearchCriteria, setAdvancedSearchCriteria] = useState<{
    promptInclude: string;
    promptExclude: string;
    negativePromptInclude: string;
    negativePromptExclude: string;
    modelIds: string[];
    aspectRatios: string[];
    dateRange: {
      start: string;
      end: string;
    };
    seedRange: {
      min: string;
      max: string;
    };
  }>({
    promptInclude: '',
    promptExclude: '',
    negativePromptInclude: '',
    negativePromptExclude: '',
    modelIds: [],
    aspectRatios: [],
    dateRange: {
      start: '',
      end: ''
    },
    seedRange: {
      min: '',
      max: ''
    }
  });
  
  // 批量操作相关函数
  const batchDownload = () => {
    if (images.length === 0) return;
    
    // 创建一个队列，逐个下载图片
    const imagesToDownload = images;
    
    // 显示下载开始提示
    alert(`开始下载 ${imagesToDownload.length} 张图片，请稍候...`);
    
    // 使用Promise.all并行下载所有图片
    Promise.all(
      imagesToDownload.map(image => 
        fetch(image.url)
          .then(response => response.blob())
          .then(blob => {
            saveAs(blob, `ai-painting-${image.id}.png`);
            return true;
          })
          .catch(error => {
            console.error('下载图像失败:', error);
            return false;
          })
      )
    ).then(results => {
      const successCount = results.filter(Boolean).length;
      alert(`下载完成！成功下载 ${successCount}/${imagesToDownload.length} 张图片。`);
    });
  };
  
  const batchDelete = () => {
    if (images.length === 0) return;
    
    if (window.confirm(`确定要删除所有 ${images.length} 张图片吗？此操作不可撤销！`)) {
      // 逐个删除所有图片
      images.forEach(image => {
        if (removeImage) {
          removeImage(image.id);
        }
      });
    }
  };
  
  const batchAddTag = (tagId: string) => {
    if (images.length === 0) return;
    
    images.forEach(image => {
      addTagToImage(image.id, tagId);
    });
    
    setBatchTagMenuOpen(false);
  };
  
  const batchAddToFolder = (folderId: string) => {
    if (images.length === 0) return;
    
    images.forEach(image => {
      addImageToFolder(image.id, folderId);
    });
    
    setBatchFolderMenuOpen(false);
  };
  
  const batchToggleFavorite = (setFavorite: boolean) => {
    if (images.length === 0) return;
    
    // 获取所有图片
    const allImages = images;
    
    // 根据当前状态切换收藏状态
    allImages.forEach(img => {
      if ((img.isFavorite && !setFavorite) || (!img.isFavorite && setFavorite)) {
        toggleFavorite(img.id);
      }
    });
    
    setBatchActionMenuOpen(false);
  };

  // 图片比较相关函数
  const enterCompareMode = () => {
    if (images.length < 2) {
      alert('请至少选择2张图片进行比较');
      return;
    }
    
    if (images.length > 4) {
      alert('最多只能同时比较4张图片');
      return;
    }
    
    const imagesToCompare = images;
    setCompareImages(imagesToCompare);
    setCompareMode(true);
  };
  
  const exitCompareMode = () => {
    setCompareMode(false);
    setCompareImages([]);
  };

  // 高级导出相关函数
  const openExportMenu = () => {
    if (images.length === 0) {
      alert('请先选择要导出的图片');
      return;
    }
    
    setExportMenuOpen(true);
  };
  
  const closeExportMenu = () => {
    setExportMenuOpen(false);
  };
  
  const exportImages = async () => {
    if (images.length === 0) return;
    
    const imagesToExport = images;
    
    // 显示导出开始提示
    alert(`开始导出 ${imagesToExport.length} 张图片，请稍候...`);
    
    // 创建一个临时的canvas元素用于格式转换
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      alert('您的浏览器不支持Canvas，无法导出图片');
      return;
    }
    
    // 使用Promise.all并行处理所有图片
    const results = await Promise.all(
      imagesToExport.map(async (image) => {
        try {
          // 加载图片
          const img = new Image();
          img.crossOrigin = 'anonymous'; // 允许跨域加载
          
          // 等待图片加载完成
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = image.url;
          });
          
          // 设置canvas尺寸
          canvas.width = img.width;
          canvas.height = img.height;
          
          // 清空canvas并绘制图片
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          
          // 根据选择的格式导出
          let mimeType: string;
          let blob: Blob;
          
          switch (exportFormat) {
            case 'jpeg':
              mimeType = 'image/jpeg';
              blob = await new Promise(resolve => {
                canvas.toBlob(b => resolve(b!), mimeType, exportQuality / 100);
              });
              break;
            case 'webp':
              mimeType = 'image/webp';
              blob = await new Promise(resolve => {
                canvas.toBlob(b => resolve(b!), mimeType, exportQuality / 100);
              });
              break;
            case 'png':
            default:
              mimeType = 'image/png';
              blob = await new Promise(resolve => {
                canvas.toBlob(b => resolve(b!), mimeType);
              });
              break;
          }
          
          // 生成文件名
          let fileName = exportNameTemplate
            .replace('{id}', image.id)
            .replace('{model}', image.model)
            .replace('{date}', new Date(image.created_at).toISOString().split('T')[0])
            .replace('{ratio}', image.aspect_ratio.replace(':', 'x'))
            .replace('{seed}', image.seed?.toString() || 'noseed');
          
          // 添加文件扩展名
          fileName += `.${exportFormat}`;
          
          // 保存文件
          saveAs(blob, fileName);
          
          return true;
        } catch (error) {
          console.error('导出图片失败:', error);
          return false;
        }
      })
    );
    
    const successCount = results.filter(Boolean).length;
    alert(`导出完成！成功导出 ${successCount}/${imagesToExport.length} 张图片。`);
    
    closeExportMenu();
  };

  // 拖拽排序相关函数
  const toggleSortMode = () => {
    if (!sortMode) {
      // 进入排序模式时，初始化排序数组
      setCustomSortedImages([...images]);
    } else {
      // 退出排序模式时清空排序数组
      setCustomSortedImages([]);
    }
    setSortMode(!sortMode);
  };
  
  const handleDragEnd = (result: DropResult) => {
    // 如果没有目标或拖拽被取消，则不做任何操作
    if (!result.destination) return;
    
    // 获取拖拽的起始位置和目标位置
    const { source, destination } = result;
    
    // 如果位置没有变化，则不做任何操作
    if (source.index === destination.index) return;
    
    // 创建新的排序数组
    const newSortedImages = Array.from(customSortedImages);
    // 移除拖拽的项
    const [removed] = newSortedImages.splice(source.index, 1);
    // 插入到新位置
    newSortedImages.splice(destination.index, 0, removed);
    
    // 更新排序数组
    setCustomSortedImages(newSortedImages);
  };

  // 加载模型数据，创建ID到名称的映射
  useEffect(() => {
    const loadModelNames = async () => {
      try {
        const models = await fetchModels();
        const nameMap: Record<string, string> = {};
        models.forEach(model => {
          nameMap[model.id] = model.name;
        });
        setModelNameMap(nameMap);
      } catch (error) {
        console.error('加载模型名称失败:', error);
      }
    };

    loadModelNames();
  }, []);

  // 获取用户友好的模型名称
  const getModelDisplayName = (modelId: string): string => {
    return modelNameMap[modelId] || modelId;
  };

  // 打开图像详情
  const openImageDetails = (image: GeneratedImage) => {
    setSelectedImage(image);
  };

  // 关闭图像详情
  const closeImageDetails = () => {
    setSelectedImage(null);
  };

  // 下载图像
  const downloadImage = (image: GeneratedImage) => {
    // 使用file-saver库下载图像
    fetch(image.url)
      .then(response => response.blob())
      .then(blob => {
        saveAs(blob, `ai-painting-${image.id}.png`);
      })
      .catch(error => {
        console.error('下载图像失败:', error);
      });
  };

  // 删除单张图片
  const handleRemoveImage = (imageId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡，避免触发图片详情
    if (window.confirm('确定要删除这张图片吗？')) {
      if (removeImage) {
        removeImage(imageId);
      }
    }
  };

  // 过滤图像
  const filteredImages = images.filter(image => {
    // 基本搜索条件
    const matchesBasicSearch = searchTerm === '' ||
      image.prompt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (image.negative_prompt && image.negative_prompt.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesModel = filterModel === '' || image.model === filterModel;
    const matchesAspectRatio = filterAspectRatio === '' || image.aspect_ratio === filterAspectRatio;

    // 日期过滤
    let matchesDate = true;
    if (filterDate !== '') {
      const imageDate = new Date(image.created_at).toLocaleDateString();
      const filterDateStr = new Date(filterDate).toLocaleDateString();
      matchesDate = imageDate === filterDateStr;
    }
    
    // 标签筛选
    let matchesTags = true;
    if (selectedTags.length > 0) {
      matchesTags = selectedTags.every(tagId => 
        image.tags && image.tags.includes(tagId)
      );
    }
    
    // 收藏夹筛选
    let matchesFolder = true;
    if (selectedFolder) {
      const folder = folders.find(f => f.id === selectedFolder);
      matchesFolder = folder ? folder.imageIds.includes(image.id) : true;
    }
    
    // 收藏筛选
    let matchesFavorite = true;
    if (showFavorites) {
      matchesFavorite = image.isFavorite === true;
    }
    
    // 高级搜索条件
    let matchesAdvancedSearch = true;
    
    // 提示词包含
    if (advancedSearchCriteria.promptInclude) {
      const terms = advancedSearchCriteria.promptInclude.toLowerCase().split(' ');
      matchesAdvancedSearch = matchesAdvancedSearch && terms.every(term => 
        image.prompt.toLowerCase().includes(term)
      );
    }
    
    // 提示词排除
    if (advancedSearchCriteria.promptExclude) {
      const terms = advancedSearchCriteria.promptExclude.toLowerCase().split(' ');
      matchesAdvancedSearch = matchesAdvancedSearch && terms.every(term => 
        !image.prompt.toLowerCase().includes(term)
      );
    }
    
    // 负面提示词包含
    if (advancedSearchCriteria.negativePromptInclude && image.negative_prompt) {
      const terms = advancedSearchCriteria.negativePromptInclude.toLowerCase().split(' ');
      matchesAdvancedSearch = matchesAdvancedSearch && terms.every(term => 
        image.negative_prompt!.toLowerCase().includes(term)
      );
    }
    
    // 负面提示词排除
    if (advancedSearchCriteria.negativePromptExclude && image.negative_prompt) {
      const terms = advancedSearchCriteria.negativePromptExclude.toLowerCase().split(' ');
      matchesAdvancedSearch = matchesAdvancedSearch && terms.every(term => 
        !image.negative_prompt!.toLowerCase().includes(term)
      );
    }
    
    // 模型筛选
    if (advancedSearchCriteria.modelIds.length > 0) {
      matchesAdvancedSearch = matchesAdvancedSearch && 
        advancedSearchCriteria.modelIds.includes(image.model);
    }
    
    // 宽高比筛选
    if (advancedSearchCriteria.aspectRatios.length > 0) {
      matchesAdvancedSearch = matchesAdvancedSearch && 
        advancedSearchCriteria.aspectRatios.includes(image.aspect_ratio);
    }
    
    // 日期范围筛选
    if (advancedSearchCriteria.dateRange.start || advancedSearchCriteria.dateRange.end) {
      const imageDate = new Date(image.created_at);
      
      if (advancedSearchCriteria.dateRange.start) {
        const startDate = new Date(advancedSearchCriteria.dateRange.start);
        matchesAdvancedSearch = matchesAdvancedSearch && imageDate >= startDate;
      }
      
      if (advancedSearchCriteria.dateRange.end) {
        const endDate = new Date(advancedSearchCriteria.dateRange.end);
        // 将结束日期设置为当天的最后一毫秒
        endDate.setHours(23, 59, 59, 999);
        matchesAdvancedSearch = matchesAdvancedSearch && imageDate <= endDate;
      }
    }
    
    // 种子值范围筛选
    if ((advancedSearchCriteria.seedRange.min || advancedSearchCriteria.seedRange.max) && image.seed !== undefined) {
      if (advancedSearchCriteria.seedRange.min) {
        const minSeed = parseInt(advancedSearchCriteria.seedRange.min);
        if (!isNaN(minSeed)) {
          matchesAdvancedSearch = matchesAdvancedSearch && image.seed >= minSeed;
        }
      }
      
      if (advancedSearchCriteria.seedRange.max) {
        const maxSeed = parseInt(advancedSearchCriteria.seedRange.max);
        if (!isNaN(maxSeed)) {
          matchesAdvancedSearch = matchesAdvancedSearch && image.seed <= maxSeed;
        }
      }
    }

    return matchesBasicSearch && matchesModel && matchesAspectRatio && 
           matchesDate && matchesTags && matchesFolder && matchesFavorite &&
           matchesAdvancedSearch;
  });

  // 高级搜索相关函数
  const toggleAdvancedSearch = () => {
    setAdvancedSearchOpen(!advancedSearchOpen);
  };
  
  const resetAdvancedSearch = () => {
    setAdvancedSearchCriteria({
      promptInclude: '',
      promptExclude: '',
      negativePromptInclude: '',
      negativePromptExclude: '',
      modelIds: [],
      aspectRatios: [],
      dateRange: {
        start: '',
        end: ''
      },
      seedRange: {
        min: '',
        max: ''
      }
    });
    
    // 重置基本搜索条件
    setSearchTerm('');
    setFilterModel('');
    setFilterAspectRatio('');
    setFilterDate('');
  };
  
  const applyAdvancedSearch = () => {
    // 关闭高级搜索面板
    setAdvancedSearchOpen(false);
  };
  
  const toggleModelFilter = (modelId: string) => {
    const currentModels = [...advancedSearchCriteria.modelIds];
    if (currentModels.includes(modelId)) {
      setAdvancedSearchCriteria({
        ...advancedSearchCriteria,
        modelIds: currentModels.filter(id => id !== modelId)
      });
    } else {
      setAdvancedSearchCriteria({
        ...advancedSearchCriteria,
        modelIds: [...currentModels, modelId]
      });
    }
  };
  
  const toggleAspectRatioFilter = (ratio: string) => {
    const currentRatios = [...advancedSearchCriteria.aspectRatios];
    if (currentRatios.includes(ratio)) {
      setAdvancedSearchCriteria({
        ...advancedSearchCriteria,
        aspectRatios: currentRatios.filter(r => r !== ratio)
      });
    } else {
      setAdvancedSearchCriteria({
        ...advancedSearchCriteria,
        aspectRatios: [...currentRatios, ratio]
      });
    }
  };

  // 获取所有唯一的模型和宽高比
  const uniqueModels = Array.from(new Set(images.map(image => image.model)));
  const uniqueAspectRatios = Array.from(new Set(images.map(image => image.aspect_ratio)));

  // 根据宽高比计算padding-bottom百分比
  const getAspectRatioPadding = (aspectRatio: string): string => {
    if (!aspectRatio) return '100%';

    const [width, height] = aspectRatio.split(':').map(Number);
    if (!width || !height) return '100%';

    return `${(height / width) * 100}%`;
  };

  // 添加自定义样式
  useEffect(() => {
    // 添加自定义滚动条样式
    const style = document.createElement('style');
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #1a1a2e;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #3a3a5c;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #4a4a6c;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // 根据宽高比计算图片尺寸
  const getImageDimensions = (aspectRatio: string): { width: number, height: number } => {
    if (!aspectRatio) return { width: 600, height: 600 };

    const [width, height] = aspectRatio.split(':').map(Number);
    if (!width || !height) return { width: 600, height: 600 };

    // 基准尺寸，保持原始宽高比
    const baseSize = 600;

    if (width >= height) {
      // 宽大于等于高，以宽为基准
      return { width: baseSize, height: Math.round(baseSize * (height / width)) };
    } else {
      // 高大于宽，以高为基准
      return { width: Math.round(baseSize * (width / height)), height: baseSize };
    }
  };

  // 打开图片预览
  const openPreview = (imageUrl: string, index: number = 0) => {
    // 如果是首次打开预览，创建图片URL数组
    if (previewImages.length === 0) {
      const urls = filteredImages.map(img => img.url);
      setPreviewImages(urls);
      // 找到当前图片在数组中的索引
      const foundIndex = urls.findIndex(url => url === imageUrl);
      setPreviewImageIndex(foundIndex >= 0 ? foundIndex : 0);
    } else {
      setPreviewImageIndex(index);
    }
    setPreviewImageUrl(imageUrl);
    setPreviewOpen(true);
  };

  // 关闭图片预览
  const closePreview = () => {
    setPreviewOpen(false);
  };

  // 导航到指定索引的图片
  const navigateToImage = (index: number) => {
    setPreviewImageIndex(index);
  };

  return (
    <div className="flex-1 p-2 md:p-3">
      {images.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="text-6xl mb-4">🖼️</div>
          <h2 className="text-xl font-semibold text-white mb-2">您的画廊是空的</h2>
          <p className="text-dark-300 max-w-md">
            生成一些精美的AI艺术作品，它们将会显示在这里。
          </p>
        </div>
      ) : (
        <>
          {/* 工具栏 */}
          <div className="mb-3 flex flex-col gap-2">
            {/* 搜索和筛选 */}
            <div className="flex flex-col md:flex-row gap-2 items-center justify-between">
              <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
                <div className="relative flex-1 md:w-64">
                  <input
                    type="text"
                    placeholder="搜索提示词..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10 w-full"
                  />
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
                </div>

                {/* 模型筛选 */}
                <select
                  value={filterModel}
                  onChange={(e) => setFilterModel(e.target.value)}
                  className="input bg-dark-700"
                >
                  <option value="">所有模型</option>
                  {uniqueModels.map(model => (
                    <option key={model} value={model}>
                      {getModelDisplayName(model)}
                    </option>
                  ))}
                </select>

                {/* 宽高比筛选 */}
                <select
                  value={filterAspectRatio}
                  onChange={(e) => setFilterAspectRatio(e.target.value)}
                  className="input bg-dark-700"
                >
                  <option value="">所有宽高比</option>
                  {uniqueAspectRatios.map(ratio => (
                    <option key={ratio} value={ratio}>
                      {ratio}
                    </option>
                  ))}
                </select>

                {/* 日期筛选 */}
                <div className="relative">
                  <input
                    type="date"
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    className="input bg-dark-700"
                    title="按日期筛选"
                  />
                  {filterDate && (
                    <button
                      onClick={() => setFilterDate('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-dark-400 hover:text-white"
                    >
                      <FaTimes size={12} />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-dark-300">共 {filteredImages.length} 张图片</span>
              <div className="flex items-center space-x-2">
                {/* 缩略图尺寸控制 */}
                <div className="flex items-center space-x-1 bg-dark-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setThumbnailSize('small')}
                    className={`p-2 ${thumbnailSize === 'small' ? 'bg-primary-600 text-white' : 'text-dark-300 hover:text-white'}`}
                    title="小图标"
                  >
                    <FaThList size={16} />
                  </button>
                  <button
                    onClick={() => setThumbnailSize('medium')}
                    className={`p-2 ${thumbnailSize === 'medium' ? 'bg-primary-600 text-white' : 'text-dark-300 hover:text-white'}`}
                    title="中图标"
                  >
                    <FaThList size={18} />
                  </button>
                  <button
                    onClick={() => setThumbnailSize('large')}
                    className={`p-2 ${thumbnailSize === 'large' ? 'bg-primary-600 text-white' : 'text-dark-300 hover:text-white'}`}
                    title="大图标"
                  >
                    <FaTh size={18} />
                  </button>
                  <button
                    onClick={() => setThumbnailSize('xlarge')}
                    className={`p-2 ${thumbnailSize === 'xlarge' ? 'bg-primary-600 text-white' : 'text-dark-300 hover:text-white'}`}
                    title="超大图标"
                  >
                    <FaThLarge size={18} />
                  </button>
                </div>
                
                {/* 清空按钮 - 只显示垃圾桶图标 */}
                <button
                  onClick={clearGallery}
                  className="btn btn-outline border-red-500 text-red-500 hover:bg-red-500"
                  title="清空画廊"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>

          {/* 图片网格 */}
          <div className={`grid gap-3 mt-2 ${
            thumbnailSize === 'small' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8' :
            thumbnailSize === 'medium' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5' :
            thumbnailSize === 'large' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' :
            'grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3'
          }`}>
            {filteredImages.map((image, index) => (
              <div key={image.id} className="relative group cursor-pointer">
                {/* 图片卡片 */}
                <div 
                  className="relative w-full h-0 overflow-hidden rounded-lg bg-dark-800 pb-[100%]"
                  onClick={() => openPreview(image.url, index)}
                >
                  <img 
                    src={image.url} 
                    alt={image.prompt.substring(0, 30) + '...'}
                    className="absolute inset-0 w-full h-full object-contain transition-opacity hover:opacity-90"
                  />
                  
                  {/* 查询详情按钮 - 鼠标悬停时才显示 */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openImageDetails(image);
                    }}
                    className="absolute top-2 right-2 p-2 rounded-full bg-dark-800 bg-opacity-70 text-white hover:bg-primary-600 transition-colors z-10 opacity-0 group-hover:opacity-100"
                    title="查询详情"
                  >
                    <FaInfoCircle />
                  </button>
                  
                  {/* 悬停时显示的操作按钮 */}
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-900 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2">
                    <div className="flex justify-between items-center">
                      <button 
                        onClick={(e) => {
                          e.stopPropagation(); // 阻止触发父元素的点击事件
                          downloadImage(image);
                        }}
                        className="p-2 rounded-full bg-dark-700 bg-opacity-70 text-white hover:bg-primary-600 transition-colors"
                        title="下载图片"
                      >
                        <FaDownload size={14} />
                      </button>
                      
                      <button 
                        onClick={(e) => handleRemoveImage(image.id, e)}
                        className="p-2 rounded-full bg-dark-700 bg-opacity-70 text-white hover:bg-red-600 transition-colors"
                        title="删除图片"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* 显示标签和宽高比 */}
                <div className="px-2 py-1 text-xs text-dark-300 flex flex-wrap items-center gap-1">
                  {/* 显示标签 */}
                  {image.tags && image.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mr-1">
                      {image.tags.slice(0, 2).map(tagId => {
                        const tag = tags.find(t => t.id === tagId);
                        if (!tag) return null;
                        return (
                          <span 
                            key={tagId}
                            className="px-1.5 py-0.5 rounded-full text-xs bg-dark-700 text-dark-200"
                            style={{ borderLeft: `2px solid ${tag.color}` }}
                            title={tag.name}
                          >
                            {tag.name.length > 6 ? tag.name.substring(0, 6) + '...' : tag.name}
                          </span>
                        );
                      })}
                      {image.tags.length > 2 && (
                        <span className="px-1.5 py-0.5 rounded-full text-xs bg-dark-700 text-dark-200">
                          +{image.tags.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* 显示宽高比和模型 */}
                  <div className="flex justify-between items-center w-full mt-1">
                    <span>{image.aspect_ratio}</span>
                    <span className="text-dark-400">{getModelDisplayName(image.model)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* 图片预览组件 */}
      <ImagePreview
        imageUrl={previewImageUrl}
        isOpen={previewOpen}
        onClose={closePreview}
        images={previewImages}
        currentIndex={previewImageIndex}
        onNavigate={navigateToImage}
      />
      
      {/* 图像详情组件 */}
      <ImageDetails
        image={selectedImage}
        isOpen={selectedImage !== null}
        onClose={closeImageDetails}
        onDownload={downloadImage}
        onToggleFavorite={toggleFavorite}
        tags={tags}
        getModelDisplayName={getModelDisplayName}
      />
    </div>
  );
};

export default Gallery;
